import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getUserData } from '../services/authService';
import { redirectToCustomerPortal, hasManageableSubscription } from '../services/stripePortalService';

function Pricing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingPortal, setProcessingPortal] = useState(false);

  const lang = i18n.language || 'fr';

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadUserData = async () => {
      const result = await getUserData(currentUser.uid);
      if (result.success) {
        setUserData(result.data);
      }
      setLoading(false);
    };

    loadUserData();
  }, [currentUser, navigate]);

  // ✅ ABONNEMENTS SILVER/GOLD
  const handleSubscribe = async (plan) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (processingPayment) return;

    setProcessingPayment(true);

    try {
      const paymentLinks = {
        silver: process.env.REACT_APP_PAYMENT_LINK_SILVER || "https://buy.stripe.com/00wfZi0wl47I5Fy9QCcAo02",
        gold: process.env.REACT_APP_PAYMENT_LINK_GOLD || "https://buy.stripe.com/6oUfZibaZ47Ic3W6EqcAo01",
      };

      const link = paymentLinks[plan];
      
      if (!link || link.includes('VOTRE_LINK')) {
        alert(lang === 'fr' 
          ? 'Erreur : Lien de paiement non configuré' 
          : 'Error: Payment link not configured');
        setProcessingPayment(false);
        return;
      }

      // ✅ SAUVEGARDER LE PLAN
      console.log('💾 Sauvegarde du plan:', plan);
      localStorage.setItem('pendingPlan', plan);

      // ✅ CONSTRUIRE L'URL AVEC client_reference_id
      const url = new URL(link);
      url.searchParams.set('client_reference_id', currentUser.uid);
      if (currentUser.email) {
        url.searchParams.set('prefilled_email', currentUser.email);
      }

      console.log('🚀 Redirection vers:', url.toString());
      window.location.href = url.toString();

    } catch (error) {
      console.error('Error:', error);
      alert(t('paymentError'));
      setProcessingPayment(false);
    }
  };

  // Gérer l'abonnement via le portail Stripe
  const handleManageSubscription = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (processingPortal) return;

    setProcessingPortal(true);

    try {
      console.log('🔧 Tentative d\'accès au portail client...');
      console.log('   → userId:', currentUser.uid);
      console.log('   → userEmail:', currentUser.email);
      
      const result = await redirectToCustomerPortal(currentUser.uid, currentUser.email);
      
      if (!result.success) {
        const errorMessage = lang === 'fr' 
          ? `Erreur lors de l'accès au portail de gestion : ${result.error || 'Erreur inconnue'}`
          : `Error accessing management portal: ${result.error || 'Unknown error'}`;
        
        console.error('❌ Erreur portail:', result.error);
        alert(errorMessage);
        setProcessingPortal(false);
      }
      // Si succès, la redirection vers Stripe se fait automatiquement
    } catch (error) {
      console.error('❌ Exception lors de l\'accès au portail:', error);
      const errorMessage = lang === 'fr'
        ? 'Une erreur est survenue. Veuillez réessayer.'
        : 'An error occurred. Please try again.';
      alert(errorMessage);
      setProcessingPortal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  const currentPlan = userData?.subscriptionType || 'free';
  const canManageSubscription = hasManageableSubscription(userData);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('levelUp')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('chooseOffer')}
          </p>
        </div>

        {/* Bannière de gestion d'abonnement pour Silver/Gold */}
        {canManageSubscription && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">⚙️</span>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-1">
                      {lang === 'fr' 
                        ? `Vous êtes actuellement abonné ${currentPlan === 'silver' ? 'Silver' : 'Gold'}`
                        : `You are currently subscribed to ${currentPlan === 'silver' ? 'Silver' : 'Gold'}`}
                    </h3>
                    <p className="text-gray-700 text-sm mb-3">
                      {lang === 'fr'
                        ? 'Gérez votre abonnement : annulez, modifiez votre plan, mettez à jour vos informations de paiement ou consultez vos factures.'
                        : 'Manage your subscription: cancel, change your plan, update payment information, or view invoices.'}
                    </p>
                    <button
                      onClick={handleManageSubscription}
                      disabled={processingPortal}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processingPortal 
                        ? (lang === 'fr' ? 'Redirection...' : 'Redirecting...') 
                        : (lang === 'fr' ? '⚙️ Gérer mon abonnement' : '⚙️ Manage my subscription')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* FREE */}
          <div className="card relative">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('freePlan')}
              </h3>
              <div className="text-4xl font-bold text-gray-900 mb-2">
                0€
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('oneFreeDiagnostic')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('reportConsultation')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">✗</span>
                <span className="text-gray-400">{t('pdfDownload')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">✗</span>
                <span className="text-gray-400">{t('libraryAccess')}</span>
              </li>
            </ul>

            {currentPlan === 'free' && (
              <div className="text-center py-3 bg-gray-100 rounded-lg font-semibold text-gray-700">
                {t('currentPlan')}
              </div>
            )}
          </div>

          {/* SILVER */}
          <div className="card relative shadow-xl">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('silverPlan')}
              </h3>
              <div className="text-4xl font-bold text-primary-600 mb-2">
                29,99€
              </div>
              <p className="text-gray-600">{t('perMonth')}</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('threeMonthlyDiagnostics')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('pdfDownload')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">✗</span>
                <span className="text-gray-400">{t('libraryAccess')}</span>
              </li>
            </ul>

            {currentPlan === 'silver' ? (
              <div className="text-center py-3 bg-primary-100 rounded-lg font-semibold text-primary-700">
                {t('currentPlan')}
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe('silver')}
                disabled={processingPayment}
                className="w-full btn-primary disabled:opacity-50"
              >
                {processingPayment ? t('redirecting') : t('chooseSilver')}
              </button>
            )}
          </div>

          {/* GOLD */}
          <div className="card relative bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400">
            <div className="absolute top-0 right-0 bg-yellow-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm font-bold">
              {t('popular')}
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('goldPlan')}
              </h3>
              <div className="text-4xl font-bold text-yellow-600 mb-2">
                49,99€
              </div>
              <p className="text-gray-600">{t('perMonth')}</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('sevenMonthlyDiagnostics')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('pdfDownload')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{t('fullLibrary')}</span>
              </li>
            </ul>

            {currentPlan === 'gold' ? (
              <div className="text-center py-3 bg-yellow-100 rounded-lg font-semibold text-yellow-700">
                {t('currentPlan')}
              </div>
            ) : (
              <button
                onClick={() => handleSubscribe('gold')}
                disabled={processingPayment}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-3 px-6 rounded-lg hover:from-yellow-600 hover:to-orange-600 transition disabled:opacity-50"
              >
                {processingPayment ? t('redirecting') : t('upgradeGold')}
              </button>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900 font-semibold"
          >
            ← {t('backDashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Pricing;